import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import React from 'react';
import * as yup from 'yup';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { FieldDescription } from '@/components/molecules/FieldDescription';
import { useTranslator } from '@/hooks/useTranslator';
import { Markdown } from '@/types/fields';

interface AcceptableChildProps {
  readonly id?: string;
  readonly label?: string;
  readonly name?: string;
  readonly value?: unknown;
}

interface Props {
  readonly name?: string;
  readonly title?: string;
  readonly label: string;
  readonly description?: string | Markdown;
  readonly help?: string;
  readonly error?: yup.ValidationError;
  readonly required?: boolean;
}

export const FormFieldWrapper: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const { children, name, title, label, description, help, error } = props;
  const tr = useTranslator();

  if (!React.isValidElement<AcceptableChildProps>(children)) {
    throw new Error('FormFieldWrapper expects a valid input element as its children');
  }

  const childProperties = React.useMemo(
    (): AcceptableChildProps => ({
      id: name,
      name: name,
      label: tr(label),
    }),
    [label, name, tr],
  );

  return (
    <Box display="flex" flexDirection="column">
      {title && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box>
            <Typography variant="subtitle1" color="text.primary">
              {tr(title)}
            </Typography>
            <FieldDescription description={description} />
          </Box>

          {help && (
            <Tooltip title={tr(help)}>
              <ButtonBase disableRipple={true}>
                <InfoIcon />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
      )}
      {/* Clone the field and add some IMPORTANT attributes to it */}
      {React.cloneElement(children, childProperties)}
      {error && error.type !== 'nullable' && (
        <FormHelperText error={true}>{error.message}</FormHelperText>
      )}
    </Box>
  );
};
